import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { ShieldX } from 'lucide-react';

const API = '/api';

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [accessDenied, setAccessDenied] = useState(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const fullUrl = window.location.href;
    const hash = window.location.hash;
    const search = window.location.search;
    const routerHash = location.hash;
    const routerSearch = location.search;

    // Also check early-captured values from index.html inline script
    const capturedSid = window.__CAPTURED_SESSION_ID__;
    const storedSid = sessionStorage.getItem('__pending_session_id__');
    const authDebug = window.__AUTH_DEBUG__;

    console.log('[AuthCallback] Full URL:', fullUrl);
    console.log('[AuthCallback] window.hash:', hash);
    console.log('[AuthCallback] window.search:', search);
    console.log('[AuthCallback] router.hash:', routerHash);
    console.log('[AuthCallback] router.search:', routerSearch);
    console.log('[AuthCallback] Captured SID:', capturedSid ? 'present' : 'none');
    console.log('[AuthCallback] Stored SID:', storedSid ? 'present' : 'none');
    console.log('[AuthCallback] Auth debug:', JSON.stringify(authDebug));

    // Try multiple sources for session_id
    let sessionId = null;

    // Source 1: window.location.hash
    let raw = hash.split('session_id=')[1];
    if (raw) sessionId = raw.split('&')[0];

    // Source 2: window.location.search
    if (!sessionId) {
      sessionId = new URLSearchParams(search).get('session_id');
    }

    // Source 3: React Router location
    if (!sessionId && routerHash) {
      raw = routerHash.split('session_id=')[1];
      if (raw) sessionId = raw.split('&')[0];
    }
    if (!sessionId && routerSearch) {
      sessionId = new URLSearchParams(routerSearch).get('session_id');
    }

    // Source 4: Early capture from index.html inline script
    if (!sessionId && capturedSid) {
      sessionId = capturedSid;
      console.log('[AuthCallback] Using captured session_id from inline script');
    }

    // Source 5: sessionStorage fallback
    if (!sessionId && storedSid) {
      sessionId = storedSid;
      console.log('[AuthCallback] Using stored session_id from sessionStorage');
    }

    // Clean up stored session_id
    sessionStorage.removeItem('__pending_session_id__');

    console.log('[AuthCallback] Final session_id:', sessionId ? sessionId.substring(0, 8) + '...' : 'NONE');

    if (!sessionId) {
      console.error('[AuthCallback] No session_id found from any source');
      setDebugInfo({
        fullUrl,
        hash,
        search,
        routerHash,
        routerSearch,
        capturedUrl: authDebug?.url || 'N/A',
        capturedHash: authDebug?.hash || 'N/A',
        error: 'No session_id in URL'
      });
      return;
    }

    const exchangeSession = async () => {
      // Step 1: Pre-warm the backend BEFORE we burn the session_id. A freshly
      // deployed pod can take 5-15s to come up; if we exchange the session_id
      // against a cold backend we get a 502/timeout and Emergent Auth's
      // session_id is then consumed — leading to the "had to try 3-4 times
      // after every deploy" pattern. Hitting /auth/me is a no-op (it just
      // returns 401 for an anonymous caller), but the 200/401 round-trip
      // confirms the pod is warm.
      const HEALTH_RETRIES = [500, 1000, 2000, 4000, 6000]; // up to ~13.5s
      for (let i = 0; i < HEALTH_RETRIES.length; i++) {
        try {
          await axios.get(`${API}/auth/me`, { withCredentials: true, timeout: 5000, validateStatus: () => true });
          console.log('[AuthCallback] Backend warm — proceeding to session exchange');
          break;
        } catch (warmupErr) {
          const isLast = i === HEALTH_RETRIES.length - 1;
          console.warn(`[AuthCallback] Warmup ping ${i + 1} failed (${warmupErr?.code || warmupErr?.message}). ${isLast ? 'Giving up on warmup.' : 'Retrying...'}`);
          if (!isLast) await new Promise(r => setTimeout(r, HEALTH_RETRIES[i]));
        }
      }

      // Step 2: Exchange the session_id. Retry only on network/5xx errors —
      // a 401 here means Emergent Auth already invalidated the session_id
      // (consumed or expired), so we MUST stop retrying or we'll silently
      // loop until timeout while the user stares at a spinner.
      const EXCHANGE_BACKOFFS = [500, 1000, 2000, 4000]; // 4 retries after the first try
      for (let attempt = 0; attempt <= EXCHANGE_BACKOFFS.length; attempt++) {
        try {
          console.log(`[AuthCallback] Exchange attempt ${attempt + 1}...`);
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true, timeout: 20000 });
          const userData = response.data;
          console.log('[AuthCallback] Exchange SUCCESS, user:', userData.email);
          localStorage.setItem('frx_user', JSON.stringify(userData));
          // Capture the session token as a Bearer fallback IMMEDIATELY at login
          // time. This is independent of the session cookie — so even if the
          // production edge/Cloudflare strips the cookie, every subsequent /api
          // request (incl. /auth/me on refresh) still authenticates via Bearer
          // and the user is NOT logged out on refresh.
          if (userData.session_token) {
            try { localStorage.setItem('frx_token', userData.session_token); } catch (e) { /* ignore */ }
          }
          setUser(userData);
          // Use window.location for hard redirect — React Router navigate() can fail with hash fragments
          window.location.replace('/dashboard');
          return;
        } catch (err) {
          const status = err?.response?.status;
          const detail = err?.response?.data?.detail || err?.message;
          const isNetworkOr5xx = !status || status >= 500;
          console.error(`[AuthCallback] Attempt ${attempt + 1} failed: ${status || '(no response)'} - ${detail}`);

          if (status === 403) {
            setAccessDenied(true);
            return;
          }
          // 401 from /auth/session means the session_id is already invalid —
          // no amount of retrying will rescue it. Bail with a clean message.
          if (status === 401) {
            setError('Login token expired before it could be exchanged. Please go back and try "Continue with Google" again.');
            return;
          }
          // Only retry on transient/server errors (502/503/504/timeout/no response).
          // For any other 4xx, the request itself is malformed; retrying would be wasteful.
          if (!isNetworkOr5xx) {
            setError(`Authentication failed (${status}). Please go back and try "Continue with Google" again.`);
            return;
          }
          if (attempt < EXCHANGE_BACKOFFS.length) {
            await new Promise(r => setTimeout(r, EXCHANGE_BACKOFFS[attempt]));
          }
        }
      }
      setError('The backend is taking longer than usual to start up. Please go back and try "Continue with Google" again — it usually works on the next attempt.');
    };

    exchangeSession();
  }, [navigate, setUser, location]);

  if (accessDenied) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="access-denied">
        <div className="text-center max-w-sm px-6">
          <ShieldX className="w-12 h-12 text-rose-400 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Access Restricted</h2>
          <p className="text-sm text-slate-500 mb-6">This platform is restricted to authorized ForesightRx team members.</p>
          <button onClick={() => navigate('/')} className="text-sm text-sky-600 hover:underline" data-testid="back-to-home">Back to home</button>
        </div>
      </div>
    );
  }

  if (debugInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-debug">
        <div className="text-center max-w-lg px-6">
          <p className="text-base font-semibold text-slate-900 mb-2">Authentication Redirect Issue</p>
          <p className="text-sm text-slate-500 mb-4">The session token was not found in the redirect URL.</p>
          <div className="text-left bg-white p-4 rounded-lg border text-xs text-slate-600 break-all space-y-1 mb-4">
            <p><strong>Current URL:</strong> {debugInfo.fullUrl}</p>
            <p><strong>Hash:</strong> {debugInfo.hash || '(empty)'}</p>
            <p><strong>Search:</strong> {debugInfo.search || '(empty)'}</p>
            <p><strong>Captured URL:</strong> {debugInfo.capturedUrl}</p>
            <p><strong>Captured Hash:</strong> {debugInfo.capturedHash}</p>
          </div>
          <button onClick={() => navigate('/login')} className="text-sm text-sky-600 hover:underline" data-testid="back-to-login">Back to Login</button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-error">
        <div className="text-center max-w-sm px-6">
          <p className="text-sm text-red-500 mb-4">{error}</p>
          <button onClick={() => navigate('/login')} className="text-sm text-sky-600 hover:underline" data-testid="back-to-login">Back to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center max-w-xs px-6">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-600 font-medium" style={{ fontFamily: 'Public Sans, sans-serif' }}>Authenticating...</p>
        <p className="text-xs text-slate-400 mt-2">If this is the first sign-in after a deployment, the backend may take a few extra seconds to warm up.</p>
      </div>
    </div>
  );
}
